import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithReAuth } from '@/api/baseApi';
import { pharmacyTags } from '@/api/pharmacy/tags';
import { PHARMACY_API } from '@/shared/api/constants';
import { mapSortToQueryParameter } from '@/utils/utils';

export const pharmacyApi = createApi({
  reducerPath: 'pharmacyApi',
  baseQuery: baseQueryWithReAuth,
  tagTypes: Object.values(pharmacyTags),
  endpoints: (builder) => ({
    getDrugs: builder.query<any, any>({
      queryFn: async (payload: any, _api, _, baseQuery: any) => {
        const result = await baseQuery({
          url: `${PHARMACY_API}/drug`,
          params: {
            ...payload?.filters,
            page: payload?.pageable?.page,
            size: payload?.pageable?.size,
            sort: mapSortToQueryParameter(payload?.sort),
          },
        });
        return { data: result?.data as any };
      },
      providesTags: [pharmacyTags.DRUGS],
    }),
    getDrugExpense: builder.query<any, any>({
      queryFn: async (payload: any, _api, _, baseQuery: any) => {
        const result = await baseQuery({
          url: `${PHARMACY_API}/drugExpense`,
          params: {
            ...payload?.filters,
            page: payload?.pageable?.page,
            size: payload?.pageable?.size,
            sort: mapSortToQueryParameter(payload?.sort),
          },
        });
        return { data: result?.data as any };
      },
      providesTags: [pharmacyTags.EXPENSE],
    }),

    persistMedicineBatch: builder.mutation({
      query: (payload: any) => ({
        url: `${PHARMACY_API}/drugBatch`,
        method: 'POST',
        body: {
          ...payload,
        },
      }),
      invalidatesTags: [pharmacyTags.DRUGS],
    }),
    updatePackagesAmount: builder.mutation({
      query: (payload: any) => ({
        url: `${PHARMACY_API}/drugBatch/${payload.id}`,
        method: 'PUT',
        body: {
          packagesAmount: payload?.packagesAmount,
          basis: payload?.basis,
        },
      }),
      invalidatesTags: [pharmacyTags.DRUGS],
    }),
    deleteBatch: builder.mutation({
      query: (payload: any) => ({
        url: `${PHARMACY_API}/drugBatch/${payload?.id}`,
        method: 'DELETE',
        params: {
          basis: payload?.basis,
        },
      }),
      invalidatesTags: [pharmacyTags.DRUGS],
    }),
    deleteDrug: builder.mutation({
      query: (payload: any) => ({
        url: `${PHARMACY_API}/drug/${payload?.id}`,
        method: 'DELETE',
        params: {
          basis: payload?.basis,
        },
      }),
      invalidatesTags: [pharmacyTags.DRUGS],
    }),
    expenseBatch: builder.mutation({
      query: (payload: any) => ({
        url: `${PHARMACY_API}/drugExpense/expenseBatch`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [
        pharmacyTags.HAS_EXPENSE,
        pharmacyTags.EXPENSE,
        pharmacyTags.DRUGS,
      ],
    }),
    getHistory: builder.query<any, any>({
      query: (payload) => ({
        url: `${PHARMACY_API}/drugBatchHistory`,
        method: 'GET',
        params: {
          ...payload.filters,
          page: payload?.paging?.page,
          size: payload?.paging?.size,
          sort: mapSortToQueryParameter(payload?.sort),
        },
      }),
      providesTags: [pharmacyTags.DRUGS],
    }),
  }),
});

export const {
  useGetDrugsQuery,
  useLazyGetDrugExpenseQuery,
  useExpenseBatchMutation,
  usePersistMedicineBatchMutation,
  useUpdatePackagesAmountMutation,
  useDeleteBatchMutation,
  useDeleteDrugMutation,
  useGetHistoryQuery,
} = pharmacyApi;
